import React, { useMemo, useState } from 'react';
import { Building2, Check, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { formatUnitLabel } from '../types';

export interface UnitTourReportUnit {
  id: number;
  name: string;
  layout_id?: number;
  layout_name?: string | null;
}

interface UnitTourReportDialogProps {
  open: boolean;
  units: UnitTourReportUnit[];
  generating: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (units: UnitTourReportUnit[]) => Promise<void> | void;
}

export const UnitTourReportDialog: React.FC<UnitTourReportDialogProps> = ({
  open,
  units,
  generating,
  onOpenChange,
  onGenerate,
}) => {
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const filteredUnits = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return units;
    return units.filter((unit) => formatUnitLabel(unit).toLowerCase().includes(normalized));
  }, [query, units]);

  const selectedUnits = useMemo(
    () => units.filter((unit) => selectedIds.has(unit.id)),
    [selectedIds, units],
  );

  const visibleIds = useMemo(() => filteredUnits.map((unit) => unit.id), [filteredUnits]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const toggleUnit = (unitId: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    if (selectedUnits.length === 0) return;
    await onGenerate(selectedUnits);
    setSelectedIds(new Set());
    setQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" strokeWidth={1.9} />
            QR Punch Walk Reports
          </DialogTitle>
          <DialogDescription>
            Choose one or more units to generate printable QR Punch Walk reports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-[11px] border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent">
              <Checkbox
                checked={
                  allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false
                }
                disabled={filteredUnits.length === 0}
                onCheckedChange={toggleVisible}
                aria-label="Select all visible units"
              />
              Select all visible
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{selectedUnits.length} selected</Badge>
              {selectedUnits.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <Command shouldFilter={false} className="rounded-[13px] border border-border bg-card">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search units or buildings..."
            />
            <CommandList className="max-h-[360px]">
              <CommandEmpty>No units found.</CommandEmpty>
              <CommandGroup heading="Units">
                {filteredUnits.map((unit) => {
                  const selected = selectedIds.has(unit.id);
                  return (
                    <CommandItem
                      key={unit.id}
                      value={`${unit.id}-${formatUnitLabel(unit)}`}
                      onSelect={() => toggleUnit(unit.id)}
                      className="cursor-pointer"
                    >
                      <Checkbox
                        checked={selected}
                        aria-label={`Select ${formatUnitLabel(unit)}`}
                        className="pointer-events-none"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{formatUnitLabel(unit)}</p>
                        {unit.layout_name && (
                          <p className="truncate text-xs text-muted-foreground">{unit.name}</p>
                        )}
                      </div>
                      {selected && <Check className="h-4 w-4 text-primary" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={selectedUnits.length === 0 || generating}
            className="gap-2"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Generate {selectedUnits.length > 0 ? `${selectedUnits.length} ` : ''}Report
            {selectedUnits.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
